/**
 * Phone API service — S3DEV2-7242
 *
 * Per-team feature flag for migrating phone operations from Phoenix_Back_End
 * to Phoenix_PhoneSystem. Each exported function takes `phoneApiUrl` (from the
 * `getPhoneApiUrl` selector on the user reducer):
 *   - When `phoneApiUrl` is non-null/non-empty: routes to that base URL using
 *     PhoneSystem's endpoint paths.
 *   - When `phoneApiUrl` is null/empty: falls back to legacy Phoenix_Back_End
 *     endpoints via `utils/api`.
 *
 * The saga is responsible for selecting `phoneApiUrl` and call context (callSid,
 * direction, etc.) and passing them in. This keeps the routing decision in one
 * place rather than scattered across sagas.
 */
import request from "superagent";
import assign from "object-assign";
import { Promise } from "es6-promise";
import * as api from "./api";

/* eslint-disable no-console */

/**
 * Internal: fire a request against the PhoneSystem base URL with the same auth
 * headers used by the Back_End api module.
 *
 * `phoneApiUrl` is the PhoneSystem service base hostname (e.g.
 * `https://phones.s3resupply-dev-app.prj.gcp.wellsky.io`). All controllers in
 * Phoenix_PhoneSystem are mounted under `/api/...`, so we prefix `api/` here
 * once instead of repeating it in every operation's path string.
 */
function phoneSystemRequest(method, phoneApiUrl, path, body) {
  const base = String(phoneApiUrl).replace(/\/$/, "");
  const suffix = String(path).replace(/^\//, "");
  const url = `${base}/api/${suffix}`;

  return new Promise((resolve, reject) => {
    let req = request[method](url).set(api.makeBaseHeaders());
    if (body !== undefined && (method === "post" || method === "put")) {
      req = req.send(body);
    }
    req.end((error, response) => {
      if (error) {
        console.error(
          `PhoneSystem API ERROR (${method.toUpperCase()} ${url}):`,
          error
        );
        reject(error);
      } else {
        resolve(response ? response.body : undefined);
      }
    });
  });
}

const shouldUsePhoneSystem = phoneApiUrl =>
  typeof phoneApiUrl === "string" && phoneApiUrl.length > 0;

// ─── Phone Operations ────────────────────────────────────────────────────

/**
 * Pause/resume the active call recording.
 * - PhoneSystem: PUT /twilio/outgoing/recording/{callSid}/pause?paused=bool
 *   (requires explicit callSid, looks up conference automatically)
 * - Back_End: POST /twilio/new/general/recording/{callDirection}/{paused}
 *   (looks up the user's current call from the auth token)
 */
export function setRecordingPause(
  phoneApiUrl,
  { callDirection, callSid, paused }
) {
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    return phoneSystemRequest(
      "put",
      phoneApiUrl,
      `twilio/outgoing/recording/${encodeURIComponent(callSid)}/pause?paused=${paused}`
    );
  }
  return api.post(
    `twilio/new/general/recording/${encodeURIComponent(callDirection)}/${paused}`
  );
}

/**
 * Redirect an active outgoing call to play an automated voicemail message,
 * then push the patient's outreach date.
 * - PhoneSystem: POST /twilio/transfer/voicemail/{phoneNumber}/{patientGuid}
 * - Back_End: POST /twilio/new/transfer/RedirectCallIntoVoicemailSay/{phoneNumber}/{patientGuid}
 */
export function sendAutomatedVoicemail(
  phoneApiUrl,
  { dialedNumber, patientGuid }
) {
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    return phoneSystemRequest(
      "post",
      phoneApiUrl,
      `twilio/transfer/voicemail/${encodeURIComponent(dialedNumber)}/${encodeURIComponent(patientGuid)}`
    );
  }
  return api.post(
    `twilio/new/transfer/RedirectCallIntoVoicemailSay/${encodeURIComponent(dialedNumber)}/${encodeURIComponent(patientGuid)}`
  );
}

/**
 * Get the current state of all phone queues for the user's team(s).
 * - PhoneSystem: GET /teams/queues (returns array of TeamQueueDto with
 *   teamId/teamName/queuedCallers/totalCallers/members[])
 * - Back_End: GET /twilio/queue (returns flat list of Twilio queue objects
 *   with friendly_name/current_size/average_wait_time/listCallTimes)
 *
 * The PhoneSystem response is mapped to the legacy shape so existing
 * components (queue-controls.jsx, etc.) and the new_getPhoneQueues selector
 * keep working without UI changes. Re-shaping here is intentional — keeps
 * the contract change scoped to this module.
 */
export function getQueueStatus(phoneApiUrl) {
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    return phoneSystemRequest("get", phoneApiUrl, "teams/queues").then(
      mapPhoneSystemQueuesToLegacy
    );
  }
  return api.get("twilio/queue");
}

function mapPhoneSystemQueuesToLegacy(teamQueues) {
  if (!Array.isArray(teamQueues)) return [];
  return teamQueues.map(t => {
    const members = Array.isArray(t.members) ? t.members : [];
    const waitTimes = members
      .map(m => Number(m.waitTimeSec))
      .filter(n => Number.isFinite(n) && n >= 0);
    // Legacy "average_wait_time" is consumed by queue-controls.jsx as the
    // headline number; keeping it as the longest current wait is the closest
    // analogue to what coaches see today (PhoneSystem doesn't track historical
    // average wait per queue).
    const longestWait = waitTimes.length > 0 ? Math.max(...waitTimes) : 0;
    return {
      friendly_name: t.teamName ?? "",
      current_size: t.totalCallers ?? 0,
      average_wait_time: longestWait,
      listCallTimes: waitTimes
    };
  });
}

/**
 * Answer a queued caller — dequeues the next eligible caller and returns
 * call context (parent call SID, patient matches, company).
 * - PhoneSystem: POST /queue/answer  body { queueName }
 * - Back_End: POST /twilio/New/Outgoing/Queue  body { to, from }
 *
 * `to` carries the team identifier in the legacy "client:{teamName}" format
 * for Back_End. PhoneSystem expects just the team name.
 */
export function answerQueueCall(phoneApiUrl, { to, from }) {
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    // Strip the legacy "client:" prefix if present so PhoneSystem sees just
    // the team name.
    const queueName =
      typeof to === "string" && to.startsWith("client:")
        ? to.slice("client:".length).trim()
        : to;
    return phoneSystemRequest("post", phoneApiUrl, "queue/answer", {
      queueName
    });
  }
  return api.post("twilio/New/Outgoing/Queue", { to, from });
}

/**
 * Initiate a warm transfer to another number/extension.
 * - PhoneSystem: PUT /twilio/transfer/call/{direction}  body { number, transferInProgress }
 * - Back_End: PUT /twilio/New/Transfer/Call/{direction}  body { number, transferInProgress }
 *
 * Same payload, different URL.
 */
export function transferCall(
  phoneApiUrl,
  { callDirection, number, transferInProgress }
) {
  const body = assign(
    { number, transferInProgress },
    {} // future-proof for additional fields without changing call sites
  );
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    return phoneSystemRequest(
      "put",
      phoneApiUrl,
      `twilio/transfer/call/${encodeURIComponent(callDirection)}`,
      body
    );
  }
  return api.put(
    `twilio/New/Transfer/Call/${encodeURIComponent(callDirection)}`,
    body
  );
}

/**
 * Cancel one or more in-flight transfer legs in the conference.
 * - PhoneSystem: POST /twilio/transfer/cancel/{direction}
 * - Back_End: POST /twilio/New/Transfer/Cancel/{direction}
 *
 * No body in either case — cancels all transfer legs.
 */
export function cancelTransfer(phoneApiUrl, { callDirection }) {
  if (shouldUsePhoneSystem(phoneApiUrl)) {
    return phoneSystemRequest(
      "post",
      phoneApiUrl,
      `twilio/transfer/cancel/${encodeURIComponent(callDirection)}`
    );
  }
  return api.post(
    `twilio/New/Transfer/Cancel/${encodeURIComponent(callDirection)}`
  );
}
